/* One controller per document. Elevate owns assignment and native prices. */
(() => {
  'use strict';
  const EXPERIMENT = '8515e13a-0842-4e63-809a-feadfb94e97b';
  const THEME = 194097938708;
  const MODE = 'aerre_three_way_mode_20261021';
  const ARMS = Object.freeze({ '58155': 'control', '58156': 'low', '58157': 'high' });
  const SLOT = '__aerreSharedRewards20261021';
  const RELOAD_KEY = 'aerre:shared-rewards:reload:v1';
  const OWNED = ['aerre_spend_price_cohort_20261019', 'aerre_spend_rewards_20261019', 'aerre_spend_cash_20261019', 'aerre_cash_30', 'aerre_cash_50'];

  function validAssignment(context, arm) {
    return context?.testId === EXPERIMENT && ARMS[String(context.variationId)] === arm &&
      context.isControl === (arm === 'control');
  }
  function cookieJSON(document, name) {
    const value = String(document.cookie || '').split(';').map(part => part.trim()).find(part => part.startsWith(name + '='));
    if (!value) return {};
    const raw = value.slice(name.length + 1);
    try { return JSON.parse(decodeURIComponent(raw)); } catch (_) {
      try { return JSON.parse(raw); } catch (_) { throw new Error('Native experiment state is unreadable'); }
    }
  }
  function nativeAuthority(window, config, request) {
    if (Number(window.Shopify?.theme?.id) !== THEME) return { status: 'reject', reason: 'wrong_theme' };
    const initial = config.initialState;
    if (initial?.country !== 'AU' || initial?.currency !== 'AUD') return { status: 'ready', profile: 'control', reason: 'outside_market' };
    if (!window.__aerreSharedNativeReady) return { status: 'pending', reason: 'native_initializing' };
    const params = new URL(window.location.href).searchParams;
    let previewId = params.get('abtid');
    try { previewId ||= window.sessionStorage.getItem('abtid'); } catch (_) {}
    const previewFlag = params.get('eabUserPreview') === 'true' || /(?:^|;\s*)eabUserPreview=true(?:;|$)/.test(window.document.cookie || '');
    const preview = previewFlag && previewId === EXPERIMENT;
    const experiment = window.eab_data?.allTests?.[EXPERIMENT];
    if (!preview && experiment?.data?.isLive !== true) return { status: 'ready', profile: 'control', reason: 'inactive_experiment' };
    const excluded = cookieJSON(window.document, 'eabExcludedTests');
    if (excluded[EXPERIMENT] === true) return request && request.arm !== 'control'
      ? { status: 'pending', reason: 'native_exclusion_conflict' }
      : { status: 'ready', profile: 'control', reason: 'excluded' };
    const assignments = cookieJSON(window.document, 'ABTL');
    const id = assignments[EXPERIMENT] == null ? null : String(assignments[EXPERIMENT]);
    if (id && !ARMS[id]) return { status: 'reject', reason: 'unknown_native_variation' };
    if (request) {
      if (!validAssignment(request.context, request.arm)) return { status: 'reject', reason: 'invalid_context' };
      if (id !== String(request.context.variationId)) return { status: 'pending', reason: 'native_context_mismatch' };
      return { status: 'ready', profile: request.arm, reason: 'native_context' };
    }
    if (id && ARMS[id] !== 'control') return { status: 'pending', reason: 'awaiting_native_context' };
    if (!id && preview) return { status: 'pending', reason: 'preview_assignment_pending' };
    return { status: 'ready', profile: 'control', reason: id ? 'native_control' : 'not_assigned' };
  }
  function modeChanges(cart, profile) {
    if (!['control', 'low', 'high'].includes(profile) || !Array.isArray(cart?.items) || !cart.attributes || typeof cart.attributes !== 'object') throw new Error('Cart mode unavailable');
    const attributes = {}, updates = {}, current = cart.attributes;
    const prior = ['low', 'high'].includes(current[MODE]) ? current[MODE] : 'control';
    if (prior !== profile || (current[MODE] && !['control', 'low', 'high'].includes(current[MODE]))) attributes[MODE] = profile;
    if (profile === 'control') {
      for (const key of OWNED) if (current[key] != null && current[key] !== '') attributes[key] = '';
      if (current.perfume50_offer_74366e03 !== 'control') attributes.perfume50_offer_74366e03 = 'control';
      const components = new Set(cart.items.flatMap(item => (item.item_components || []).map(child => child.key)));
      for (const item of cart.items) {
        if (item.properties?._aerre_threshold_gift !== 'spend_rewards_20261019') continue;
        if (!item.key || item.item_components?.length || item.parent_relationship || components.has(item.key) || item.properties?._aerre_rewards_gift) throw new Error('Ambiguous reward gift; cart unchanged');
        updates[item.key] = 0;
      }
    } else {
      if (current.aerre_spend_price_cohort_20261019 !== profile) attributes.aerre_spend_price_cohort_20261019 = profile;
      if (prior !== profile || current.aerre_spend_price_cohort_20261019 !== profile) {
        for (const key of ['aerre_cash_30', 'aerre_cash_50', 'aerre_spend_cash_20261019']) if (current[key]) attributes[key] = '';
      }
    }
    return { attributes, updates };
  }
  function hasChanges(changes) { return Boolean(Object.keys(changes.attributes).length || Object.keys(changes.updates).length); }

  function createManager(window, config, adapters = {}) {
    const document = window.document;
    let request = null, controller = null, copy = null, queue = Promise.resolve(), reloadStarted = false, disposed = false;
    let originalCheckout = null, originalReadState = null;
    const api = { version: 'shared-three-way-v1', status: 'pending', profile: config.profile, controller: null, ready: null, assign, revalidate, dispose, reason: '' };
    const now = adapters.now || (() => Date.now());
    const delay = adapters.delay || (ms => new Promise(resolve => window.setTimeout(resolve, ms)));
    const cartURL = new URL(config.cartUrl || '/cart', window.location.origin);
    if (cartURL.origin !== window.location.origin) throw new Error('Invalid cart origin');
    async function requestJSON(url, options = {}) {
      const abort = new window.AbortController();
      const timeout = window.setTimeout(() => abort.abort(), 15000);
      try {
        const response = await window.fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: abort.signal });
        if (!response.ok) throw new Error('Cart request failed');
        return await response.json();
      } finally { window.clearTimeout(timeout); }
    }
    const readCart = adapters.readCart || (() => requestJSON(cartURL.pathname.replace(/\/$/, '') + '.js'));
    const writeCart = adapters.writeCart || (payload => requestJSON(cartURL.pathname.replace(/\/$/, '') + '/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }));
    const loadController = adapters.loadController || (async profile => {
      const kind = profile === 'control' ? 'control' : 'spend';
      if (!window.__aerreThresholdFactories?.[kind]) {
        const url = new URL(config.controllerAssets?.[kind], window.location.href);
        if (url.protocol !== 'https:' || ![window.location.hostname, 'cdn.shopify.com'].includes(url.hostname)) throw new Error('Invalid controller asset');
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url.href; script.async = true;
          const timer = window.setTimeout(() => reject(new Error('Reward assets timed out')), 15000);
          script.onload = () => { window.clearTimeout(timer); resolve(); };
          script.onerror = () => { window.clearTimeout(timer); reject(new Error('Reward assets unavailable')); };
          document.head.appendChild(script);
        });
      }
      const factory = window.__aerreThresholdFactories?.[kind];
      if (typeof factory !== 'function') throw new Error('Reward controller unavailable');
      return factory(config);
    });
    function setStatus(status, reason = '') {
      api.status = status; api.reason = reason;
      const banner = document.getElementById?.('aerre-shared-offer-status');
      if (banner) banner.hidden = status === 'ready' || status === 'disposed';
    }
    function showStatus(error = false) {
      let banner = document.getElementById?.('aerre-shared-offer-status');
      if (!banner && document.body && document.createElement) {
        banner = document.createElement('div'); banner.id = 'aerre-shared-offer-status';
        banner.setAttribute('role', error ? 'alert' : 'status'); banner.setAttribute('aria-live', 'polite');
        banner.style.cssText = 'position:fixed;bottom:12px;left:12px;right:12px;z-index:100000;background:white;color:#222;border:1px solid #777;padding:14px;box-shadow:0 2px 8px #0003;font-size:14px';
        document.body.appendChild(banner);
      }
      if (banner) {
        banner.replaceChildren(document.createTextNode(error ? 'We could not confirm your offers. Please refresh and try again. ' : 'Confirming your offers. Please try again in a moment.'));
        if (error) { const button = document.createElement('button'); button.textContent = 'Refresh'; button.type = 'button'; button.onclick = () => window.location.reload(); banner.appendChild(button); }
        banner.hidden = false;
      }
    }
    function fail(error) {
      if (reloadStarted || disposed) return;
      stopController();
      setStatus('error', error?.message || 'Offer could not be confirmed');
      showStatus(true);
      document.querySelectorAll?.('[data-aerre-gift-status]').forEach(node => { node.textContent = 'We could not confirm your offers. Please refresh the page and try again.'; node.hidden = false; });
      window.console?.error('Reward profile verification failed', error);
    }
    function enqueue(operation) { const task = queue.then(operation, operation); queue = task.catch(() => {}); return task; }
    function authority() { return nativeAuthority(window, config, request); }
    function stopController() { copy?.destroy(); copy = null; controller?.dispose?.(); }
    function reload(profile, cause) {
      if (reloadStarted || disposed) return;
      stopController();
      const key = `${EXPERIMENT}:${config.profile}:${profile}:${cause}`;
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(RELOAD_KEY) || 'null');
        if (saved?.key === key && now() - saved.at < 60000) throw new Error('Offer did not render after one reload');
        window.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ key, at: now() }));
      } catch (error) { fail(error); return; }
      reloadStarted = true; setStatus('reloading', cause);
      window.location.reload();
    }
    async function normalize(profile) {
      let changed = false;
      for (let attempt = 0; attempt <= 3; attempt++) {
        if (disposed) throw new Error('Manager retired');
        const cart = await readCart();
        const changes = modeChanges(cart, profile);
        if (!hasChanges(changes)) return { cart, changed };
        if (attempt === 3) break;
        const payload = {};
        if (Object.keys(changes.attributes).length) payload.attributes = changes.attributes;
        if (Object.keys(changes.updates).length) payload.updates = changes.updates;
        await writeCart(payload); changed = true;
      }
      throw new Error('Offer mode did not persist');
    }
    async function activate(profile) {
      if (reloadStarted || disposed) return false;
      setStatus('checking');
      if (controller) { stopController(); await controller.waitForCartWrites?.(); await controller.queue?.catch(() => {}); }
      const normalized = await normalize(profile);
      if (disposed) return false;
      const latest = authority();
      if (latest.status === 'ready' && latest.profile !== profile) return activate(latest.profile);
      if (latest.status !== 'ready') throw new Error('Native assignment changed during cart synchronization');
      if (profile !== config.profile) { reload(profile, 'profile'); return false; }
      if (controller) { reload(profile, 'cart_drift'); return false; }
      controller = await loadController(profile); api.controller = controller;
      if (disposed) { stopController(); return false; }
      if (typeof controller.dispose !== 'function') throw new Error('Controller lifecycle unavailable');
      window.aerreThresholdOffer = controller;
      originalReadState = controller.readState;
      controller.readState = async function (...args) {
        const state = await originalReadState.apply(this, args);
        if (state.profile !== config.profile) {
          stopController();
          void enqueue(() => activate(profile)).catch(fail);
          throw Object.assign(new Error('Cart profile changed'), { code: 'CHECKOUT_HANDOFF' });
        }
        return state;
      };
      originalCheckout = controller.checkout;
      controller.checkout = async function (...args) {
        try {
          const current = authority();
          if (current.status !== 'ready') throw new Error('Native assignment is not confirmed');
          const cart = await readCart();
          if (current.profile !== profile || hasChanges(modeChanges(cart, profile))) {
            await enqueue(() => activate(current.profile)); return;
          }
          return originalCheckout.apply(this, args);
        } catch (error) { fail(error); }
      };
      await controller.start();
      if (controller.disposed || reloadStarted || disposed) return false;
      const confirmed = authority();
      if (confirmed.status !== 'ready' || confirmed.profile !== profile) throw new Error('Native assignment changed while offers loaded');
      if (controller.state?.profile !== profile) throw new Error('Rendered and cart profiles differ');
      if (normalized.changed) await controller.publish(controller.state);
      if (profile !== 'control' && typeof window.createAerreSpendPriceCopy === 'function') copy = window.createAerreSpendPriceCopy({ cohort: profile, getState: () => controller.state });
      try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (_) {}
      setStatus('ready'); api.profile = profile; return true;
    }
    function assign(context, arm) {
      if (disposed || !validAssignment(context, arm)) return false;
      request = { context: { testId: context.testId, variationId: String(context.variationId), isControl: context.isControl }, arm };
      if (api.status === 'ready' || api.status === 'error') void revalidate().catch(fail);
      return true;
    }
    function revalidate() {
      return enqueue(async () => {
        if (reloadStarted || disposed) return;
        const current = authority();
        if (current.status !== 'ready') throw new Error('Native assignment is not confirmed');
        const cart = await readCart();
        if (api.status === 'error' || current.profile !== api.profile || hasChanges(modeChanges(cart, current.profile))) return activate(current.profile);
        return true;
      });
    }
    function gatedTarget(target) {
      return target?.closest?.('form[action*="/cart"] button[type="submit"], form[action*="/cart"] input[type="submit"], line-item-quantity, [data-aerre-gift-open], [data-aerre-gift-choose], [data-aerre-priority-checkout], [name="checkout"], a[href*="/checkout"], a[href*="/cart/change"], .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [data-shopify-buttoncontainer]');
    }
    function gate(event) {
      const targets = event.composedPath?.() || [event.target];
      const gated = targets.some(gatedTarget) || (event.type === 'submit' && event.target?.matches?.('form[action*="/cart"], form[action*="/checkout"]'));
      if (api.status === 'ready' || !gated) return;
      event.preventDefault(); event.stopImmediatePropagation();
      showStatus(api.status === 'error');
    }
    function onPageShow(event) {
      if (!event.persisted) return;
      event.stopImmediatePropagation?.();
      reload(api.profile, 'bfcache');
    }
    function onVisible() { if (document.visibilityState === 'visible' && api.status === 'ready') void revalidate().catch(fail); }
    function onRender() { if (api.status === 'ready') copy?.render(); }
    function dispose() {
      disposed = true;
      stopController(); setStatus('disposed');
      document.removeEventListener?.('click', gate, true); document.removeEventListener?.('submit', gate, true);
      document.removeEventListener?.('visibilitychange', onVisible);
      document.documentElement?.removeEventListener?.('aerre:threshold-state', onRender);
      window.removeEventListener?.('pageshow', onPageShow);
    }
    document.addEventListener?.('click', gate, true); document.addEventListener?.('submit', gate, true);
    document.addEventListener?.('visibilitychange', onVisible);
    document.documentElement?.addEventListener?.('aerre:threshold-state', onRender);
    window.addEventListener?.('pageshow', onPageShow);
    for (const entry of window.__aerreSharedRewardAssignments || []) assign(entry.context, entry.arm);
    window.__aerreSharedRewardAssignments = [];
    api.ready = (async () => {
      const deadline = now() + 15000;
      while (!disposed && now() < deadline) {
        const current = authority();
        if (current.status === 'reject') throw new Error(current.reason);
        if (current.status === 'ready') return enqueue(() => activate(current.profile));
        api.reason = current.reason; await delay(50);
      }
      if (!disposed) throw new Error('Native offer assignment timed out');
    })();
    api.ready.catch(fail);
    return api;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { EXPERIMENT, THEME, MODE, ARMS, validAssignment, nativeAuthority, modeChanges, createManager };
  } else {
    if (Number(window.Shopify?.theme?.id) !== THEME || window.Shopify?.designMode || window[SLOT]) return;
    const node = document.getElementById('aerre-threshold-config');
    if (!node) return;
    const config = JSON.parse(node.textContent);
    const state = document.querySelector('script[data-aerre-threshold-state]');
    if (!config.initialState && state) config.initialState = JSON.parse(state.textContent);
    window[SLOT] = createManager(window, config);
  }
})();
